import { useEffect, useState } from 'react';
import { modificadoresService, productosService } from '../../services/api';
import { btnPrimario, btnSecundario } from '../../components/ui/estilos';
import { Campo, dosColumnas, entrada } from '../agenda/campos';
import RecetaProducto from './RecetaProducto';

// Áreas fijas a propósito: el backend imprime iterando ['COCINA','BARRA'].
const AREAS = ['COCINA', 'BARRA', 'CAJA'];
const n = (v: any) => parseFloat(v || 0) || 0;

// Comprime a JPEG de máx. 400 px de lado para no inflar la base.
function comprimirImagen(file: File, maxLado = 400, calidad = 0.72): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxLado) { height = Math.round(height * maxLado / width); width = maxLado; }
        else if (height > maxLado) { width = Math.round(width * maxLado / height); height = maxLado; }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('No se pudo procesar la imagen'));
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', calidad));
      };
      img.onerror = () => reject(new Error('Imagen inválida'));
      img.src = reader.result as string;
    };
    reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    reader.readAsDataURL(file);
  });
}

const deProducto = (p: any) => ({
  categoria_id: p.categoria_id, nombre: p.nombre, clave: p.clave || '', precio: String(p.precio), iva: String(p.iva ?? '16'),
  unidad: p.unidad || 'PIEZA', area_impresion: p.area_impresion || 'COCINA', precio_abierto: !!p.precio_abierto,
  suspendido: !!p.suspendido, disponible: p.disponible !== false, imagen: p.imagen || '',
});

function Interruptor({ label, sub, valor, onCambio }: { label: string; sub: string; valor: boolean; onCambio: (v: boolean) => void }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
      <input type="checkbox" checked={valor} onChange={e => onCambio(e.target.checked)} style={{ width: 22, height: 22, accentColor: 'var(--brand-texto)', flex: 'none' }} />
      <span><span style={{ display: 'block', fontSize: 15, fontWeight: 700 }}>{label}</span><span style={{ fontSize: 13, color: 'var(--muted)' }}>{sub}</span></span>
    </label>
  );
}

export default function EditorProducto({ producto, categorias, unidades, grupos, say, onCambio, onEliminado }: {
  producto: any; categorias: any[]; unidades: string[]; grupos: any[];
  say: (m: string) => void; onCambio: () => void; onEliminado: () => void;
}) {
  const [tab, setTab] = useState<'info' | 'mods' | 'receta'>('info');
  const [f, setF] = useState(deProducto(producto));
  const [asignados, setAsignados] = useState<string[]>([]);

  useEffect(() => {
    setF(deProducto(producto));
    modificadoresService.getDeProducto(producto.id).then(r => setAsignados(r.data.map((g: any) => g.id))).catch(() => setAsignados([]));
  }, [producto]);

  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF(x => ({ ...x, [k]: v }));
  const cambio = JSON.stringify(f) !== JSON.stringify(deProducto(producto));

  const guardar = async (datos = f) => {
    if (!datos.nombre.trim()) { say('El producto necesita nombre.'); return; }
    if (!datos.precio_abierto && !(n(datos.precio) > 0)) { say('Captura un precio mayor a $0.'); return; }
    try {
      await productosService.actualizar(producto.id, { ...datos, nombre: datos.nombre.trim(), precio: n(datos.precio), iva: n(datos.iva) });
      say('Producto guardado.'); onCambio();
    } catch (e: any) { say(e.response?.data?.error || 'No se pudo guardar.'); }
  };

  const eliminar = async () => {
    if (!window.confirm(`¿Eliminar "${producto.nombre}"?`)) return;
    try { await productosService.eliminar(producto.id); say(`${producto.nombre} eliminado.`); onEliminado(); }
    catch (e: any) {
      // Con ventas en su historial no se puede borrar: se ofrece ocultarlo.
      if (e.response?.status === 400 && window.confirm(`${e.response.data?.error || 'No se puede eliminar.'}\n\n¿Lo ocultamos del menú?`)) guardar({ ...f, suspendido: true });
      else if (e.response?.status !== 400) say(e.response?.data?.error || 'No se pudo eliminar.');
    }
  };

  const elegirImagen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { say('Elige un archivo de imagen.'); return; }
    try { set('imagen', await comprimirImagen(file)); } catch (err: any) { say(err.message); }
  };

  const guardarGrupos = async (ids: string[]) => {
    setAsignados(ids);
    try { await modificadoresService.setGruposProducto(producto.id, ids.map((grupo_id, orden) => ({ grupo_id, orden }))); }
    catch { say('No se pudieron guardar los modificadores.'); }
  };

  const tabs: [typeof tab, string][] = [['info', 'Info'], ['mods', `Modificadores · ${asignados.length}`], ['receta', 'Receta']];

  return (
    <>
      <div role="tablist" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', borderBottom: '1px solid var(--line)', flex: 'none' }}>
        {tabs.map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} style={{ height: 48, border: 'none', background: 'var(--surface)', fontSize: 14, fontWeight: 700, cursor: 'pointer',
            color: tab === k ? 'var(--ink)' : 'var(--muted)', boxShadow: tab === k ? 'inset 0 -3px 0 var(--brand-texto)' : 'none' }}>{l}</button>
        ))}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {tab === 'info' && <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {f.imagen
              ? <img src={f.imagen} alt="" style={{ width: 72, height: 72, borderRadius: 10, objectFit: 'cover', border: '1px solid var(--line)' }} />
              : <div style={{ width: 72, height: 72, borderRadius: 10, border: '1px dashed var(--line-strong)', background: 'var(--bg)' }} />}
            <label style={{ ...btnSecundario, height: 40, fontSize: 14, display: 'inline-flex', alignItems: 'center' }}>
              {f.imagen ? 'Cambiar imagen' : 'Subir imagen'}
              <input type="file" accept="image/*" onChange={elegirImagen} style={{ display: 'none' }} />
            </label>
            {f.imagen && <button onClick={() => set('imagen', '')} style={{ background: 'none', border: 'none', color: 'var(--danger)', fontWeight: 700, cursor: 'pointer' }}>Quitar</button>}
          </div>
          <Campo label="Nombre"><input value={f.nombre} onChange={e => set('nombre', e.target.value)} maxLength={120} style={entrada} /></Campo>
          <div style={dosColumnas}>
            <Campo label="Precio"><input type="number" min="0" inputMode="decimal" value={f.precio} onChange={e => set('precio', e.target.value)} style={entrada} /></Campo>
            <Campo label="Clave"><input value={f.clave} onChange={e => set('clave', e.target.value)} maxLength={30} placeholder="ENT-001" style={entrada} /></Campo>
          </div>
          <div style={dosColumnas}>
            <Campo label="Categoría">
              <select value={f.categoria_id} onChange={e => set('categoria_id', e.target.value)} style={entrada}>{categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}</select>
            </Campo>
            <Campo label="Se prepara en">
              <select value={f.area_impresion} onChange={e => set('area_impresion', e.target.value)} style={entrada}>{AREAS.map(a => <option key={a} value={a}>{a[0] + a.slice(1).toLowerCase()}</option>)}</select>
            </Campo>
          </div>
          <div style={dosColumnas}>
            <Campo label="IVA %"><input type="number" min="0" max="100" value={f.iva} onChange={e => set('iva', e.target.value)} style={entrada} /></Campo>
            <Campo label="Unidad"><select value={f.unidad} onChange={e => set('unidad', e.target.value)} style={entrada}>{unidades.map(u => <option key={u}>{u}</option>)}</select></Campo>
          </div>
          <Interruptor label="Disponible hoy" sub="Quítalo si se acabó; en el salón sale como agotado." valor={f.disponible} onCambio={v => set('disponible', v)} />
          <Interruptor label="Precio abierto" sub="El precio se captura al vender." valor={f.precio_abierto} onCambio={v => set('precio_abierto', v)} />
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {cambio && <button onClick={() => guardar()} style={{ ...btnPrimario, height: 48, fontSize: 15 }}>Guardar cambios</button>}
            <button onClick={() => guardar({ ...f, suspendido: !f.suspendido })}
              style={f.suspendido ? { ...btnPrimario, height: 44, fontSize: 14 } : { ...btnSecundario, height: 44, fontSize: 14 }}>
              {f.suspendido ? 'Mostrar en el menú' : 'Ocultar del menú'}
            </button>
            <button onClick={eliminar} style={{ background: 'none', border: 'none', fontSize: 14, fontWeight: 700, color: 'var(--danger)', cursor: 'pointer', height: 44 }}>Eliminar producto</button>
          </div>
        </>}

        {tab === 'mods' && <>
          <div style={{ fontSize: 14, color: 'var(--muted)' }}>Grupos que se piden al agregar {producto.nombre} a un pedido.</div>
          {asignados.map(id => {
            const g = grupos.find(x => x.id === id);
            return (
              <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--line)' }}>
                <span style={{ flex: 1 }}><b>{g?.nombre || 'Grupo'}</b> <span style={{ color: 'var(--muted)', fontSize: 14 }}>{g?.obligatorio ? '· obligatorio' : ''}</span></span>
                <button onClick={() => guardarGrupos(asignados.filter(x => x !== id))} aria-label={`Quitar ${g?.nombre}`} style={{ ...btnSecundario, width: 36, height: 36, padding: 0 }}>✕</button>
              </div>
            );
          })}
          {!asignados.length && <div style={{ color: 'var(--muted)' }}>Sin modificadores.</div>}
          <select value="" onChange={e => e.target.value && guardarGrupos([...asignados, e.target.value])} aria-label="Agregar grupo" style={{ ...entrada, height: 46 }}>
            <option value="">+ Agregar grupo…</option>
            {grupos.filter(g => !asignados.includes(g.id)).map(g => <option key={g.id} value={g.id}>{g.nombre}</option>)}
          </select>
        </>}

        {tab === 'receta' && <RecetaProducto producto={producto} say={say} />}
      </div>
    </>
  );
}
